import React, { useEffect, useState } from 'react';
import { ViewProps } from 'react-native';
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { Platform } from 'src/libs/platform';
import { IOS_EASING, ANDROID_EASING } from 'src/themes/constants';

export type ScaleIntroProps = ViewProps & {
  visible: boolean;
  onEnd?: () => void;
};

const EASING = Platform.OS === 'ios' ? IOS_EASING : ANDROID_EASING;
const DURATION = Platform.select({
  ios: 300,
  android: 200,
  // @todo reanimated has an issue with timings less than 250ms in web
  // @see https://github.com/software-mansion/react-native-reanimated/issues/1450
  default: 300,
});

export const ScaleIntro: React.FC<ScaleIntroProps> = ({
  children,
  visible,
  onEnd,
  style,
  ...props
}) => {
  const [shouldRender, toggle] = useState(false);
  const opacity = useSharedValue(0);
  const scale = useSharedValue(Platform.OS === 'ios' ? 1.05 : 0.95);

  const transition = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ scale: scale.value }],
  }));

  const onAnimationEnd = () => {
    if (onEnd) onEnd();
  };

  useEffect(() => {
    if (visible && !shouldRender) {
      toggle(true);
      opacity.value = withTiming(1);
      scale.value = withTiming(1, { duration: DURATION, easing: EASING });
    }

    if (!visible && shouldRender) {
      opacity.value = withTiming(0, {}, () => runOnJS(onAnimationEnd)());
      scale.value = withTiming(Platform.OS === 'ios' ? 1.05 : 0.95, {
        duration: DURATION,
        easing: EASING,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, shouldRender]);

  return !shouldRender ? null : (
    <Animated.View style={[style, transition]} {...props}>
      {children}
    </Animated.View>
  );
};
